import React from 'react'
import { Link, useParams } from 'react-router-dom'
import Panel from '../../components/Panel'
import Badge from '../../components/Badge'
import QueryError from './QueryError'
import ConductorFieldKey from './ConductorFieldKey'
import { VerdictLine } from './conductorVerdicts'
import { TD, TH } from './conductorTableCells'
import {
  formatConductorDate,
  formatConductorStage,
  formatConductorTimestamp,
  isConductorNotFound,
  sortWorkItemsForDetail,
  tenantAccountPath,
} from './conductorUtils'
import { useAuth0 } from '@auth0/auth0-react'
import WorkItemAssignmentActions from './WorkItemAssignmentActions'
import { useGetConductorInstanceQuery } from './conductorApi'

const Loading: React.FC = () => (
  <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
)

const BackToPortfolio: React.FC = () => (
  <Link to="/conductor" className="text-sm text-indigo-600 hover:underline dark:text-indigo-400">
    Back to Portfolio
  </Link>
)

export const InstanceDetail: React.FC = () => {
  const { user } = useAuth0()
  const currentUserEmail = user?.email?.trim() ?? ''
  const { cuid } = useParams()
  const { data, isError, error, refetch } = useGetConductorInstanceQuery(cuid ?? '', {
    skip: cuid == null || cuid === '',
  })

  if (isError && isConductorNotFound(error)) {
    return (
      <div className="space-y-4 p-4">
        <BackToPortfolio />
        <div className="py-6 text-sm text-slate-600 dark:text-slate-300">Workflow not found</div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="space-y-4 p-4">
        <BackToPortfolio />
        <QueryError message="Failed to load workflow." onRetry={() => void refetch()} />
      </div>
    )
  }

  if (data == null) {
    return (
      <div className="space-y-4 p-4">
        <BackToPortfolio />
        <Loading />
      </div>
    )
  }

  const tenantHref = tenantAccountPath(data)
  const workItems = sortWorkItemsForDetail(data.workItems)

  return (
    <div className="space-y-4 p-4">
      <BackToPortfolio />
      <div>
        <h2 className="m-0 text-lg font-semibold text-slate-900 dark:text-slate-100">
          {data.playbookKey} v{data.playbookVersion}
        </h2>
        <ConductorFieldKey screen="instance" />
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
        <Badge status={data.status} />
        <span>{formatConductorStage(data.currentStage)}</span>
        {data.blockedReason != null && (
          <span className="text-red-600 dark:text-red-400">{data.blockedReason}</span>
        )}
      </div>

      <div className="text-sm">
        {tenantHref != null ? (
          <Link to={tenantHref} className="text-indigo-600 hover:underline dark:text-indigo-400">
            {data.accountNumber}
          </Link>
        ) : (
          <>
            <span className="font-mono">{data.subjectCuid}</span>
            <span className="ml-1 text-xs text-slate-400 dark:text-slate-500">{data.subjectType}</span>
          </>
        )}
      </div>

      <Panel header="Gate verdicts">
        {data.subjectVerdicts.length === 0 ? (
          <div className="text-xs text-slate-400 dark:text-slate-500">No verdicts recorded.</div>
        ) : (
          <ul className="space-y-1">
            {data.subjectVerdicts.map((verdict) => (
              <VerdictLine key={verdict.gateId} verdict={verdict} />
            ))}
          </ul>
        )}
      </Panel>

      <Panel header="Steps">
        <table className="w-full">
          <thead>
            <tr>
              <TH>Key</TH>
              <TH>Gate</TH>
              <TH>Status</TH>
              <TH>Started</TH>
              <TH>Completed</TH>
            </tr>
          </thead>
          <tbody>
            {data.steps.map((step) => (
              <tr key={step.stepKey} className="border-t border-slate-100 dark:border-slate-700">
                <TD className="font-mono">{step.stepKey}</TD>
                <TD className="font-mono">{step.gateKey ?? '—'}</TD>
                <TD>
                  <Badge status={step.status} />
                </TD>
                <TD>{step.startedAt != null ? formatConductorTimestamp(step.startedAt) : '—'}</TD>
                <TD>{step.completedAt != null ? formatConductorTimestamp(step.completedAt) : '—'}</TD>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel header="Work items">
        <table className="w-full">
          <thead>
            <tr>
              <TH>Title</TH>
              <TH>Type</TH>
              <TH>Status</TH>
              <TH>Role</TH>
              <TH>Assignee</TH>
              <TH>Due</TH>
              <TH>Resolution</TH>
            </tr>
          </thead>
          <tbody>
            {workItems.map((item) => (
              <tr key={item.cuid} className="border-t border-slate-100 dark:border-slate-700">
                <TD className="font-medium">{item.title}</TD>
                <TD>
                  <Badge status={item.type} />
                </TD>
                <TD>
                  <Badge status={item.status} />
                </TD>
                <TD>{item.ownerRole}</TD>
                <TD>
                  <div className="space-y-2">
                    <div>{item.assigneeUserId ?? '—'}</div>
                    <WorkItemAssignmentActions item={item} currentUserEmail={currentUserEmail} />
                  </div>
                </TD>
                <TD>{item.dueAt != null ? formatConductorDate(item.dueAt) : '—'}</TD>
                <TD>{item.resolution ?? '—'}</TD>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel header="Timers">
        <table className="w-full">
          <thead>
            <tr>
              <TH>Step</TH>
              <TH>Type</TH>
              <TH>Status</TH>
              <TH>Fire at</TH>
            </tr>
          </thead>
          <tbody>
            {data.timers.map((timer) => (
              <tr key={timer.cuid} className="border-t border-slate-100 dark:border-slate-700">
                <TD className="font-mono">{timer.stepKey ?? '—'}</TD>
                <TD>
                  {timer.type}
                  {timer.sovereign === true && (
                    <span className="ml-2 text-xs font-medium text-slate-500 dark:text-slate-400">
                      sovereign
                    </span>
                  )}
                </TD>
                <TD>
                  <Badge status={timer.status} />
                </TD>
                <TD>{formatConductorTimestamp(timer.fireAt)}</TD>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel header="Recent events">
        {data.recentEvents.length === 0 ? (
          <div className="text-xs text-slate-400 dark:text-slate-500">No events yet.</div>
        ) : (
          <ul className="space-y-1">
            {data.recentEvents.map((event) => (
              <li key={event.cuid} className="text-xs text-slate-600 dark:text-slate-300">
                <span className="font-mono">{event.type}</span>
                <span className="ml-2 text-slate-400 dark:text-slate-500">
                  {event.actorType} · {formatConductorTimestamp(event.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  )
}

export default InstanceDetail
